import { useEffect, useState } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import { usePanelToast } from '../../../context/PanelToastContext';
import { fetchPaymentGateway, savePaymentGateway } from '../../../services/owner/platformApi';
import {
  defaultPaymentMethods,
  defaultApiKeys,
  defaultBankAccount,
  defaultMerchantInfo,
  defaultApiConnections,
  paymentMethodMeta,
  methodConfigFields,
} from './paymentGatewayData';
import '../ownerPages.css';
import '../subscriptions/subscriptions.css';
import '../../admin/payment-methods/paymentMethods.css';
import './paymentGateway.css';

function ToggleSetting({ checked, onChange }) {
  return (
    <label className="notification-toggle admin-payment-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="notification-toggle-slider" />
    </label>
  );
}

const methodIcons = {
  bank: 'BK',
  jazzcash: 'JC',
  easypaisa: 'EP',
  stripe: 'ST',
  square: 'SQ',
  manual: 'MN',
};

function validateApiCredentials(key, apiKeys) {
  if (key === 'stripe') {
    if (!apiKeys.stripePublishableKey?.trim() || !apiKeys.stripeSecretKey?.trim()) {
      return 'Stripe Publishable Key and Secret Key are required to connect via API.';
    }
  }
  if (key === 'square') {
    if (!apiKeys.squareApplicationId?.trim() || !apiKeys.squareAccessToken?.trim() || !apiKeys.squareLocationId?.trim()) {
      return 'Square Application ID, Access Token, and Location ID are required to connect via API.';
    }
  }
  return '';
}

function ConfigField({ field, methodKey, value, onChange }) {
  const id = `${methodKey}-${field.name}`;

  if (field.type === 'textarea') {
    return (
      <textarea
        id={id}
        className="owner-form-input owner-form-textarea"
        rows={field.rows || 3}
        value={value}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (field.type === 'select') {
    return (
      <select
        id={id}
        className="owner-form-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {field.options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  return (
    <input
      id={id}
      type={field.type || 'text'}
      className="owner-form-input"
      value={value}
      placeholder={field.placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function PaymentGateway() {
  const toast = usePanelToast();
  const [methods, setMethods] = useState(defaultPaymentMethods);
  const [apiKeys, setApiKeys] = useState(defaultApiKeys);
  const [bankAccount, setBankAccount] = useState(defaultBankAccount);
  const [merchantInfo, setMerchantInfo] = useState(defaultMerchantInfo);
  const [apiConnections, setApiConnections] = useState(defaultApiConnections);
  const [configureKey, setConfigureKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [connectingKey, setConnectingKey] = useState(null);
  const [connectError, setConnectError] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchPaymentGateway();
        if (cancelled) return;
        setMethods({ ...defaultPaymentMethods, ...data.methods });
        setApiKeys({ ...defaultApiKeys, ...data.apiKeys });
        setBankAccount({ ...defaultBankAccount, ...data.bankAccount });
        setMerchantInfo({ ...defaultMerchantInfo, ...data.merchantInfo });
        setApiConnections({ ...defaultApiConnections, ...data.apiConnections });
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load payment gateway');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleMethod = (key, value) => {
    setMethods((prev) => ({ ...prev, [key]: value }));
    setSavedKey(null);
  };

  const getConfigSource = (field) => field.source || field.group;

  const getConfigValue = (field) => {
    const source = getConfigSource(field);
    if (source === 'bank') return bankAccount[field.name];
    if (source === 'api') return apiKeys[field.name];
    return merchantInfo[field.name];
  };

  const handleConfigChange = (field, value) => {
    const source = getConfigSource(field);
    if (source === 'bank') {
      setBankAccount((prev) => ({ ...prev, [field.name]: value }));
    } else if (source === 'api') {
      setApiKeys((prev) => ({ ...prev, [field.name]: value }));
    } else {
      setMerchantInfo((prev) => ({ ...prev, [field.name]: value }));
    }
    setSavedKey(null);
    setConnectError('');
    setTestMessage('');
  };

  const handleConfigure = (key) => {
    setConfigureKey((prev) => (prev === key ? null : key));
    setSavedKey(null);
    setConnectError('');
    setTestMessage('');
  };

  const handleConnectApi = (key) => {
    const error = validateApiCredentials(key, apiKeys);
    if (error) {
      setConnectError(error);
      return;
    }

    setConnectingKey(key);
    setConnectError('');
    setTestMessage('');

    window.setTimeout(async () => {
      const nextConnections = { ...apiConnections, [key]: true };
      const nextMethods = { ...methods, [key]: true };
      setApiConnections(nextConnections);
      setMethods(nextMethods);
      setConnectingKey(null);
      try {
        setSaving(true);
        await savePaymentGateway({
          methods: nextMethods,
          apiKeys,
          bankAccount,
          merchantInfo,
          apiConnections: nextConnections,
        });
        setSavedKey(key);
        toast.showSaved('Payment gateway');
        window.setTimeout(() => setSavedKey(null), 3000);
      } catch (err) {
        toast.showError(err.message || 'Failed to save connection');
      } finally {
        setSaving(false);
      }
    }, 800);
  };

  const handleDisconnectApi = (key) => {
    setApiConnections((prev) => ({ ...prev, [key]: false }));
    setMethods((prev) => ({ ...prev, [key]: false }));
    setConnectError('');
    setTestMessage('');
  };

  const handleTestApi = (key) => {
    const error = validateApiCredentials(key, apiKeys);
    if (error) {
      setConnectError(error);
      return;
    }

    setConnectingKey(key);
    setConnectError('');
    setTestMessage('');

    window.setTimeout(() => {
      setConnectingKey(null);
      setTestMessage(`${key === 'stripe' ? 'Stripe' : 'Square'} API connection successful.`);
    }, 600);
  };

  const persistGateway = async (key) => {
    try {
      setSaving(true);
      await savePaymentGateway({ methods, apiKeys, bankAccount, merchantInfo, apiConnections });
      setSavedKey(key);
      toast.showSaved('Payment gateway');
      window.setTimeout(() => setSavedKey(null), 3000);
    } catch (err) {
      toast.showError(err.message || 'Failed to save payment gateway');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMethod = (key) => {
    persistGateway(key);
  };

  return (
    <div className="admin-payment-page">
      <PageHeader title="Payment Gateway" subtitle="Configure payment methods and merchant settings" />
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading payment gateway...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="owner-card rec-panel-card admin-payment-section">
        <h2 className="owner-card-title">Payment Methods</h2>
        <div className="admin-payment-methods-list">
          {paymentMethodMeta.map((method) => {
            const isOpen = configureKey === method.key;
            const fields = methodConfigFields[method.key] || [];
            const isApiMethod = method.connectType === 'api';
            const isConnected = apiConnections[method.key];

            return (
              <div key={method.key} className="admin-payment-method-item">
                {isOpen && (
                  <div className="admin-payment-config-panel">
                    <div className="admin-payment-config-panel-header">
                      <h3 className="admin-payment-config-title">
                        {isApiMethod ? `Connect ${method.label.replace('Enable ', '')} via API` : `Configure ${method.label.replace('Enable ', '')}`}
                      </h3>
                      <button type="button" className="subscription-modal-close" onClick={() => setConfigureKey(null)} aria-label="Close">×</button>
                    </div>

                    {isApiMethod && (
                      <div className="admin-payment-api-connect">
                        <p className="admin-payment-api-hint">
                          Enter your {method.label.replace('Enable ', '')} API credentials from the developer dashboard, then connect to enable payments.
                        </p>
                        <div className="admin-payment-api-status">
                          {isConnected ? (
                            <span className="owner-badge owner-badge-success">Connected via API</span>
                          ) : (
                            <span className="owner-badge owner-badge-warning">Not connected</span>
                          )}
                          {testMessage && configureKey === method.key && (
                            <span className="admin-payment-api-test-msg">{testMessage}</span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="admin-payment-config-grid">
                      {fields.map((field) => (
                        <div key={field.name} className={`owner-form-group${field.full ? ' admin-payment-config-full' : ''}`}>
                          <label className="owner-form-label" htmlFor={`${method.key}-${field.name}`}>{field.label}</label>
                          <ConfigField
                            field={field}
                            methodKey={method.key}
                            value={getConfigValue(field)}
                            onChange={(value) => handleConfigChange(field, value)}
                          />
                        </div>
                      ))}
                    </div>

                    {connectError && configureKey === method.key && (
                      <p className="admin-payment-api-error">{connectError}</p>
                    )}

                    <div className="admin-payment-config-actions">
                      {savedKey === method.key && (
                        <span className="admin-payment-saved-msg">Configuration saved successfully</span>
                      )}
                      {isApiMethod && (
                        <>
                          <button
                            type="button"
                            className="owner-btn owner-btn-primary"
                            onClick={() => handleConnectApi(method.key)}
                            disabled={connectingKey === method.key}
                          >
                            {connectingKey === method.key ? 'Connecting...' : isConnected ? 'Reconnect via API' : 'Connect via API'}
                          </button>
                          <button
                            type="button"
                            className="owner-btn owner-btn-secondary"
                            onClick={() => handleTestApi(method.key)}
                            disabled={connectingKey === method.key}
                          >
                            Test Connection
                          </button>
                          {isConnected && (
                            <button
                              type="button"
                              className="owner-btn owner-btn-secondary"
                              onClick={() => handleDisconnectApi(method.key)}
                            >
                              Disconnect
                            </button>
                          )}
                        </>
                      )}
                      <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setConfigureKey(null)}>Cancel</button>
                      {!isApiMethod && (
                        <button type="button" className="owner-btn owner-btn-primary" onClick={() => handleSaveMethod(method.key)} disabled={saving}>
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className={`admin-payment-method-card${methods[method.key] ? ' enabled' : ''}`}>
                  <div className="admin-payment-method-main">
                    <div className="payment-method-icon">{methodIcons[method.icon]}</div>
                    <div className="admin-payment-method-text">
                      <div className="payment-method-label">{method.label.replace('Enable ', '')}</div>
                      <div className="payment-method-desc">{method.description}</div>
                      {isApiMethod && isConnected && (
                        <span className="admin-payment-api-connected-tag">API Connected</span>
                      )}
                    </div>
                    <span className={`owner-badge ${methods[method.key] ? 'owner-badge-success' : 'owner-badge-warning'}`}>
                      {methods[method.key] ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="admin-payment-method-actions">
                    <ToggleSetting checked={methods[method.key]} onChange={(v) => toggleMethod(method.key, v)} />
                    <button
                      type="button"
                      className={`owner-btn owner-btn-sm ${isOpen ? 'owner-btn-primary' : 'owner-btn-secondary'}`}
                      onClick={() => handleConfigure(method.key)}
                    >
                      {isApiMethod ? 'Connect API' : 'Configure'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PaymentGateway;
